import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import {
  Button,
  ButtonVariant,
  LoadingBar,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { Modal } from "../../../../components/modal/Modal";
import WarningModal from "../../../../components/ui-kit/modal/WarningModal";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Section = styled.div`
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
`;

const SectionTitle = styled(Typography)`
  font-weight: 700;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 13px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const InfoCell = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 10px 14px;
`;

const InfoLabel = styled.div`
  font-size: 11px;
  color: #8a9bb0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #2c3e50;
  font-weight: 600;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7a8d;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f5f5f5;
  color: #2c3e50;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ status }) => {
    if (status === "finalized") return "#e6f4ea";
    if (status === "finalized_with_warnings") return "#fff8e1";
    if (status === "draft") return "#e3f2fd";
    if (status === "void") return "#f5f5f5";
    if (status === "failed") return "#fdecea";
    return "#f5f5f5";
  }};
  color: ${({ status }) => {
    if (status === "finalized") return "#1e7e34";
    if (status === "finalized_with_warnings") return "#b45309";
    if (status === "draft") return "#1565c0";
    if (status === "void") return "#666";
    if (status === "failed") return "#c62828";
    return "#666";
  }};
`;

const WarnBanner = styled.div`
  padding: 10px 14px;
  border-radius: 6px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #6b5a00;
  font-size: 13px;
`;

const EmptyMsg = styled.div`
  padding: 24px;
  text-align: center;
  color: #8a9bb0;
  font-size: 13px;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (pence?: number) =>
  pence != null ? `£${(pence / 100).toFixed(2)}` : "—";

const fmtDate = (iso?: string) =>
  iso ? DateTime.fromISO(iso).toFormat("dd MMM yyyy") : "—";

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 3 }, (_, i) => currentYear - i);
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  companyId: string;
}

export const CompanyFeeInvoicesTab: React.FC<Props> = ({ companyId }) => {
  const { showNotification } = useNotification();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [preview, setPreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  const [generating, setGenerating] = useState(false);
  const [finalizing, setFinalizing] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState<string | null>(null);

  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    body: string;
    confirmText: string;
    onConfirm: () => void;
  } | null>(null);

  const loadInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const data = await api.listCompanyFeeInvoices(companyId, { limit: 20 });
      setInvoices(data?.docs ?? []);
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "Could not load invoices"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoadingInvoices(false);
    }
  }, [companyId, showNotification]);

  useEffect(() => {
    void loadInvoices();
  }, [loadInvoices]);

  const handlePreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const data = await api.previewCompanyFeeInvoice(companyId, year, month);
      setPreview(data);
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "Preview failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.generateCompanyFeeInvoice(companyId, { year, month });
      showNotification("Invoice draft created", NotificationTypes.SUCCESS);
      void loadInvoices();
      setPreview(null);
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "Generate failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setGenerating(false);
    }
  };

  const doFinalize = async (invoiceId: string, hasMissing: boolean) => {
    setFinalizing(invoiceId);
    try {
      await api.finalizeCompanyFeeInvoice(companyId, invoiceId, {
        acceptMissingFees: hasMissing,
      });
      showNotification("Invoice finalized", NotificationTypes.SUCCESS);
      void loadInvoices();
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "Finalize failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setFinalizing(null);
    }
  };

  const handleFinalize = (invoiceId: string, hasMissing: boolean) => {
    if (hasMissing) {
      setConfirmModal({
        title: "Finalize with warnings",
        body: "This invoice has missing fees. Totals may be understated. Finalize with warnings anyway?",
        confirmText: "Finalize",
        onConfirm: () => void doFinalize(invoiceId, true),
      });
    } else {
      void doFinalize(invoiceId, false);
    }
  };

  const doVoid = async (invoiceId: string) => {
    setVoiding(invoiceId);
    try {
      await api.voidCompanyFeeInvoice(companyId, invoiceId);
      showNotification("Invoice voided", NotificationTypes.SUCCESS);
      void loadInvoices();
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "Void failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setVoiding(null);
    }
  };

  const handleVoid = (invoiceId: string) => {
    setConfirmModal({
      title: "Void invoice",
      body: "Void this invoice? This allows regenerating a new draft for the same period.",
      confirmText: "Void",
      onConfirm: () => void doVoid(invoiceId),
    });
  };

  const handleOpenPdf = async (
    invoiceId: string,
    includeBreakdown: boolean,
    cachedUrl?: string
  ) => {
    if (cachedUrl) {
      window.open(cachedUrl, "_blank");
      return;
    }
    setGeneratingPdf(invoiceId);
    try {
      const result = await api.generateCompanyFeeInvoicePdf(
        companyId,
        invoiceId,
        includeBreakdown
      );
      if (result?.fileUrl) {
        window.open(result.fileUrl, "_blank");
        showNotification("PDF generated", NotificationTypes.SUCCESS);
        void loadInvoices();
      } else {
        showNotification(
          "PDF generated but no URL returned",
          NotificationTypes.WARNING
        );
      }
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "PDF generation failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setGeneratingPdf(null);
    }
  };

  const isCurrentMonth =
    year === now.getFullYear() && month === now.getMonth() + 1;

  const isFutureMonth =
    year > now.getFullYear() ||
    (year === now.getFullYear() && month > now.getMonth() + 1);

  return (
    <>
      <Wrap>
        {/* ─ Preview / Generate ─ */}
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Monthly fee invoice
          </SectionTitle>
          <Row>
            <Select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
            <Select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={i + 1}>
                  {m}
                </option>
              ))}
            </Select>
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={() => void handlePreview()}
              loading={loadingPreview}
            >
              Preview
            </Button>
            {!isCurrentMonth && !isFutureMonth && (
              <Button
                variant={ButtonVariant.CONTAINED}
                onClick={() => void handleGenerate()}
                loading={generating}
              >
                Generate draft
              </Button>
            )}
          </Row>

          {(isCurrentMonth || isFutureMonth) && (
            <WarnBanner>
              {isFutureMonth
                ? "Future month — preview only, cannot generate until month closes."
                : "Current month — preview only, cannot generate until month closes."}
            </WarnBanner>
          )}

          {preview && (
            <>
              {preview.missingAdminFeeCount > 0 && (
                <WarnBanner>
                  {preview.missingAdminFeeCount} payment(s) missing admin fee
                  snapshot — will be excluded from totals.
                </WarnBanner>
              )}
              {preview.missingProcessingFeeCount > 0 && (
                <WarnBanner>
                  {preview.missingProcessingFeeCount} payment(s) missing
                  processing fee — totals may be understated until Ryft settles.
                </WarnBanner>
              )}
              <InfoGrid>
                <InfoCell>
                  <InfoLabel>Payments</InfoLabel>
                  <InfoValue>{preview.paymentCount}</InfoValue>
                </InfoCell>
                <InfoCell>
                  <InfoLabel>Gross paid</InfoLabel>
                  <InfoValue>{fmt(preview.grossPaidPence)}</InfoValue>
                </InfoCell>
                <InfoCell>
                  <InfoLabel>Admin fees</InfoLabel>
                  <InfoValue>{fmt(preview.adminFeePence)}</InfoValue>
                </InfoCell>
                <InfoCell>
                  <InfoLabel>Est. refunded fees</InfoLabel>
                  <InfoValue>
                    -
                    {fmt(
                      preview.adminFeeRefundedPence ??
                        preview.estimatedAdminFeeRefundedPence
                    )}
                  </InfoValue>
                </InfoCell>
                <InfoCell>
                  <InfoLabel>Net admin fee</InfoLabel>
                  <InfoValue>{fmt(preview.netAdminFeePence)}</InfoValue>
                </InfoCell>
                <InfoCell>
                  <InfoLabel>Processing fees (gross)</InfoLabel>
                  <InfoValue>{fmt(preview.processingFeePence)}</InfoValue>
                </InfoCell>
                <InfoCell>
                  <InfoLabel>Est. refunded processing</InfoLabel>
                  <InfoValue>
                    -{fmt(preview.processingFeeRefundedPence)}
                  </InfoValue>
                </InfoCell>
                <InfoCell>
                  <InfoLabel>Net processing fee</InfoLabel>
                  <InfoValue>{fmt(preview.netProcessingFeePence)}</InfoValue>
                </InfoCell>
                <InfoCell style={{ gridColumn: "1 / -1" }}>
                  <InfoLabel>Total fees</InfoLabel>
                  <InfoValue style={{ fontSize: 18 }}>
                    {fmt(preview.totalFeesPence)}
                  </InfoValue>
                </InfoCell>
              </InfoGrid>
            </>
          )}
        </Section>

        {/* ─ Invoice list ─ */}
        <Section>
          <SectionTitle variant={TypographyVariant.HEADER3}>
            Invoice history
          </SectionTitle>
          {loadingInvoices ? (
            <LoadingBar loading />
          ) : invoices.length === 0 ? (
            <EmptyMsg>No invoices yet</EmptyMsg>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Reference</Th>
                  <Th>Period</Th>
                  <Th>Status</Th>
                  <Th>Total fees</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv: any) => {
                  const meta = inv.metadata ?? {};
                  const summary = meta.summary ?? {};
                  const hasMissing =
                    (meta.missingAdminFeeCount ?? 0) > 0 ||
                    (meta.missingProcessingFeeCount ?? 0) > 0;
                  const isBusy =
                    finalizing === inv._id ||
                    voiding === inv._id ||
                    generatingPdf === inv._id;

                  return (
                    <tr key={inv._id}>
                      <Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {inv.referenceNumber ?? inv._id}
                      </Td>
                      <Td>
                        {fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}
                      </Td>
                      <Td>
                        <StatusBadge status={inv.status}>
                          {inv.status}
                        </StatusBadge>
                      </Td>
                      <Td>{fmt(summary.totalFeesPence)}</Td>
                      <Td>
                        <Row>
                          {inv.status === "draft" && (
                            <>
                              <Button
                                variant={ButtonVariant.CONTAINED}
                                onClick={() =>
                                  handleFinalize(inv._id, hasMissing)
                                }
                                loading={finalizing === inv._id}
                                disabled={isBusy}
                              >
                                Finalise
                              </Button>
                              <Button
                                variant={ButtonVariant.OUTLINED}
                                onClick={() => handleVoid(inv._id)}
                                loading={voiding === inv._id}
                                disabled={isBusy}
                              >
                                Void
                              </Button>
                            </>
                          )}
                          {(inv.status === "finalized" ||
                            inv.status === "finalized_with_warnings") && (
                            <>
                              <Button
                                variant={
                                  inv.fileUrl
                                    ? ButtonVariant.OUTLINED
                                    : ButtonVariant.CONTAINED
                                }
                                onClick={() =>
                                  void handleOpenPdf(
                                    inv._id,
                                    false,
                                    inv.fileUrl
                                  )
                                }
                                loading={generatingPdf === inv._id}
                                disabled={isBusy}
                              >
                                {inv.fileUrl
                                  ? "PDF (merchant)"
                                  : "Generate PDF (merchant)"}
                              </Button>
                              <Button
                                variant={
                                  inv.metadata?.fileUrlWithBreakdown
                                    ? ButtonVariant.OUTLINED
                                    : ButtonVariant.CONTAINED
                                }
                                onClick={() =>
                                  void handleOpenPdf(
                                    inv._id,
                                    true,
                                    inv.metadata?.fileUrlWithBreakdown
                                  )
                                }
                                loading={generatingPdf === inv._id}
                                disabled={isBusy}
                              >
                                {inv.metadata?.fileUrlWithBreakdown
                                  ? "PDF (admin breakdown)"
                                  : "Generate PDF (admin breakdown)"}
                              </Button>
                              <Button
                                variant={ButtonVariant.OUTLINED}
                                onClick={() => handleVoid(inv._id)}
                                loading={voiding === inv._id}
                                disabled={isBusy}
                              >
                                Void
                              </Button>
                            </>
                          )}
                        </Row>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Section>
      </Wrap>

      {confirmModal && (
        <Modal onHide={() => setConfirmModal(null)}>
          <WarningModal
            headerTitle={confirmModal.title}
            mainText={confirmModal.body}
            cancelButtonText="Cancel"
            deleteButtonText={confirmModal.confirmText}
            onCancel={() => setConfirmModal(null)}
            onContinue={() => {
              confirmModal.onConfirm();
              setConfirmModal(null);
            }}
          />
        </Modal>
      )}
    </>
  );
};
